import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';

import {
    getDnsZone,
    getProviderConnectionCapabilities,
    listDnsRecords,
} from './queries';

import {applyDnsRecordChanges} from './mutations';

import {
    ApplyDnsRecordChanges,
    ApplyDnsRecordChangesInput,
    DnsRecordsConnectionResult,
    DnsRecordsFilterInput,
    DnsWriteResult,
    ListDnsRecords,
} from '../types';

const client = generateClient();

// FNP-1192 — the records service.
//
// ⚠️ ERRORS ARE RE-THROWN, NEVER SWALLOWED. Catching and returning undefined
// makes a failed fetch indistinguishable from an empty zone to React Query:
// `isError` stays false and the table renders its empty state, so "the API is
// down" and "this zone has no records" look identical. They are the two states
// this module most needs to tell apart.
//
// On the WRITE path the reason is sharper still: a rejected write carries the
// provider's refusal (PERMISSION_DENIED for a read-only credential above all),
// and that refusal is the most valuable thing the call produced. The mutation
// hook classifies it; stripping it here would leave nothing to classify.

/** What the connection reports it can do, as this module needs it. */
export type RecordWriteCapabilities = {
    // ⚠️ The types the PROVIDER supports, per THIS credential. The create form's
    // picker offers exactly these, so an unsupported type is never offered
    // rather than being offered and rejected by the provider at submit.
    supportedRecordTypes: string[],
    canWriteRecords: boolean,
}

type GetProviderConnectionCapabilities = {
    getProviderConnection?: {
        id: string,
        status: string,
        capabilities?: {
            supportedRecordTypes?: string[] | null,
            canWriteRecords?: boolean | null,
        } | null,
    } | null
}

type GetDnsZoneName = {
    getDnsZone?: {
        id: string,
        name: string,
        connectionId: string,
        provider: string,
        nameservers: string[],
    } | null
}

/**
 * One page of records in a zone.
 *
 * ⚠️ BOTH ID ARGUMENTS ARE REQUIRED (`ID!`) — a zone id is only meaningful
 * against the connection that minted it.
 *
 * ⚠️ THE FILTER IS SERVER-SIDE, and passing it changes what paging means.
 * `limit` applies BEFORE the filter on this platform, so a filtered first page
 * routinely comes back with ZERO items and a live `nextToken` holding every
 * match. Callers must branch on the token and never on `items.length`.
 */
export const fetchRecords = async (
    connectionId: string,
    zoneId: string,
    filter: DnsRecordsFilterInput | undefined,
    limit: number,
    nextToken: string | null,
): Promise<DnsRecordsConnectionResult> => {

    const response = await client.graphql<GraphQLQuery<ListDnsRecords>>({
        query: listDnsRecords,
        variables: {
            connectionId,
            zoneId,
            // Omitted entirely when nothing is set rather than sent as an empty
            // object: an all-null filter is not the same request as no filter,
            // and only one of them is what an unfiltered table means.
            filter: filter && Object.keys(filter).length > 0 ? filter : undefined,
            limit,
            nextToken,
        },
    });

    return {
        items: response.data?.listDnsRecords?.items ?? [],
        // ⚠️ `?? null` and NOT `|| null`: coercing an empty-string token to null
        // would end pagination early.
        nextToken: response.data?.listDnsRecords?.nextToken ?? null,
    };
};

/** The zone's NAME, so the page and the delete dialog can name what they act on. */
export const fetchZoneName = async (
    connectionId: string,
    zoneId: string,
): Promise<string | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetDnsZoneName>>({
        query: getDnsZone,
        variables: {
            connectionId,
            id: zoneId,
        },
    });

    return response.data?.getDnsZone?.name;
};

/**
 * What this connection's credential can do with records.
 *
 * Undefined means "could not be determined", and every caller treats that as
 * NOT permitted — see useRecordWriteCapabilities for why that direction.
 *
 * ⚠️ A CONNECTION THAT IS NOT ACTIVE REPORTS NOTHING, whatever its stored
 * snapshot says. The snapshot records what the credential proved AT CONNECT
 * TIME; an INVALID or UNREACHABLE status is the provider saying that credential
 * does not work NOW. Offering a write control against it produces a failure at
 * submit instead of an honest absence.
 */
export const fetchConnectionCapabilities = async (
    connectionId: string,
): Promise<RecordWriteCapabilities | undefined> => {

    const response = await client.graphql<GraphQLQuery<GetProviderConnectionCapabilities>>({
        query: getProviderConnectionCapabilities,
        variables: {id: connectionId},
    });

    const connection = response.data?.getProviderConnection;

    if (!connection) {
        return undefined;
    }

    if (connection.status !== 'ACTIVE') {
        return undefined;
    }

    const capabilities = connection.capabilities;

    if (!capabilities) {
        return undefined;
    }

    return {
        supportedRecordTypes: capabilities.supportedRecordTypes ?? [],
        canWriteRecords: capabilities.canWriteRecords === true,
    };
};

/**
 * Apply a batch of record changes.
 *
 * ⚠️ THE RESULT IS NULLABLE ON THE SCHEMA, deliberately: a non-null field that
 * resolves null nulls its whole parent on this platform, and the honest failure
 * mode for a refused write is an error entry plus null — not a fabricated
 * result claiming a state nothing observed. So `undefined` here means the write
 * did not report a state, and the caller must not invent one.
 *
 * ⚠️ A SUCCESSFUL RESULT IS NOT A LIVE RECORD. Read `writeState` before telling
 * a user anything.
 */
export const applyRecordChanges = async (
    input: ApplyDnsRecordChangesInput,
): Promise<DnsWriteResult | undefined> => {

    const response = await client.graphql<GraphQLQuery<ApplyDnsRecordChanges>>({
        query: applyDnsRecordChanges,
        variables: {input},
    });

    return response.data?.applyDnsRecordChanges ?? undefined;
};
